/**
 * Research Library Hook - Three-Tier Content Browser
 * 
 * Provides access to:
 * - Peer-reviewed sources (719 studies)
 * - Resources (17 interpreters: Attia, Huberman, etc.)
 * - Frameworks (7 protocols: VDOT, McGill Big 3, etc.)
 * - Consensus principles (10 universal truths)
 * 
 * Author: Coach Cedar Development Team
 * Date: December 11, 2025
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { storage } from '../services/storage';
import { isNativeSwrCacheEnabled } from '../services/featureFlagsSync';
import { getApiBaseUrl } from '../utils/api';

// Helper function to get auth headers
const getAuthHeaders = async () => {
  const token = await storage.getItem('jwt_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// ============================================
// TYPES
// ============================================

export interface PeerReviewedSource {
  id: string;
  title: string;
  authors: string[];
  journal: string;
  publication_year: number;
  doi: string;
  key_findings: string[];
  evidence_strength: 'strong' | 'moderate' | 'emerging' | 'preliminary';
  consensus_level: number;
  goal_contexts: string[];
  sport_contexts: string[];
  condition_contexts: string[];
  demographic_contexts: string[];
  type: 'peer_reviewed';
}

export interface Resource {
  id: string;
  name: string;
  credentials: string[];
  primary_expertise: string[];
  platform_types: string[];
  primary_works: any[];
  academic_background: boolean;
  peer_recognition_score: number;
  primary_lens: string[];
  consensus_alignment_score: number;
  bio?: string;
  notable_achievements?: string[];
  external_links?: {
    website?: string;
    podcast?: string;
    youtube?: string;
    amazon?: string;
    twitter?: string;
    instagram?: string;
  };
  type: 'resource';
}

export interface Framework {
  id: string;
  framework_name: string;
  // Coverage Sprint Phase F (Apr 2026): institutional frameworks
  // (ACSM/AHA/ACOG/ADA committee statements) have resource_id NULL —
  // they're committee outputs without a single named author. The
  // /api/research-library/my-frameworks endpoint LEFT JOINs on
  // research_resources, returning null for these fields. Frontend must
  // null-check before rendering "From: <name>" attribution and use
  // source_work as the fallback citation.
  resource_id: string | null;
  resource_name: string | null;
  resource_credentials: string[] | null;
  source_work: string;
  description: string;
  key_principles: string[];
  practical_guidelines: string[];
  evidence_basis: 'research_backed' | 'peer_reviewed_consensus' | 'research_lead' | 'experience_based' | 'theoretical' | 'mixed';
  consensus_alignment_score: number;
  /** API may return consensus_level (e.g. 0.95); used in LibraryTab metadata */
  consensus_level?: number;
  goal_contexts: string[];
  sport_contexts: string[];
  demographic_contexts: string[];
  // 🆕 2026-04-29: Framework credibility surface (migration 1231 + framework_credibility_service)
  credibility_statement?: string | null;
  external_citations?: ExternalCitation[];
  academic_source_count?: number;
  external_citation_count?: number;
  type: 'framework';
}

/**
 * External citations (books, podcasts, expert authorities) attached to a framework.
 *
 * NOTE 2026-04-29: The current backfill produced low-quality structured data
 * (0 authors, 0 URLs, ~half have volume-number "titles"). UI v1 does NOT render
 * this field — the type is exposed so the API contract is stable for v2 once
 * the parser is rebuilt. Keep `raw` as the display fallback when v2 lands.
 */
export interface ExternalCitation {
  type: 'book' | 'podcast' | 'conference' | 'expert' | 'website' | 'journal_paper' | 'endorsement';
  title?: string;
  author?: string;
  year?: number | null;
  publisher?: string;
  url?: string;
  notes?: string;
  raw?: string;
}

/**
 * Knowledge-type-agnostic research source.
 *
 * Returned by GET /api/research-library/sources/{id} and the academic_sources
 * array from GET /api/research-library/frameworks/{id}/sources. The native
 * ResearchSourceSheet renders different fields based on knowledge_type:
 *   - 'research'             → academic mode: DOI, abstract, journal
 *   - 'practitioner_*'       → practitioner mode: source_url, source_type
 */
export interface ResearchSource {
  id: string;
  title: string;
  authors: string[];
  journal: string | null;
  publication_year: number | null;
  doi: string | null;
  abstract: string | null;
  key_findings: string[];
  evidence_strength: 'strong' | 'moderate' | 'emerging' | 'preliminary' | null;
  methodology_type: string | null;
  source_tier: 1 | 2 | 3 | 4 | null;
  knowledge_type: 'research' | 'practitioner_fact' | 'practitioner_insight' | 'practitioner_standard';
  quality_score: number | null;
  source_url: string | null;
  source_type: string | null;
}

export interface ConsensusPrinciple {
  id: string;
  principle_name: string;
  principle_statement: string;
  consensus_level: number;
  supporting_evidence_count: number;
  primary_research_sources: string[];
  type: 'consensus_principle';
}

export interface ResourceWithFrameworks extends Resource {
  frameworks?: Framework[];
}

// ============================================
// HOOK: PEER-REVIEWED SOURCES (ALL)
// ============================================

// ============================================
// HOOK: USER-CITED SOURCES (ONLY SOURCES USED FOR THIS USER)
// ============================================

export interface UserCitedSource extends PeerReviewedSource {
  citation_count: number;
}

export const useUserCitedSources = (limit: number = 50, offset: number = 0) => {
  const [data, setData] = useState<{
    sources: UserCitedSource[];
    total_count: number;
    limit: number;
    offset: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/api/research-library/my-sources?limit=${limit}&offset=${offset}`, { headers });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load your cited sources');
      }
      const data = await response.json();
      setData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load your cited sources');
      console.error('Error fetching user sources:', err);
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  return { data, loading, error, refetch: fetchSources };
};

// ============================================
// HOOK: RESOURCES (INTERPRETERS)
// ============================================

export const useResources = () => {
  const [data, setData] = useState<{
    resources: Resource[];
    total_count: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/api/research-library/resources`, { headers });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load resources');
      }
      const data = await response.json();
      setData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load resources');
      console.error('Error fetching resources:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  return { data, loading, error, refetch: fetchResources };
};

// ============================================
// HOOK: FRAMEWORKS
// ============================================

// ============================================
// HOOK: MY FRAMEWORKS (ONLY FRAMEWORKS CITED FOR THIS USER)
// ============================================

export const useMyFrameworks = () => {
  const [data, setData] = useState<{
    frameworks: Framework[];
    total_count: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFrameworks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/api/research-library/my-frameworks`, { headers });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load your frameworks');
      }
      const data = await response.json();
      setData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load your frameworks');
      console.error('Error fetching my frameworks:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFrameworks();
  }, [fetchFrameworks]);

  return { data, loading, error, refetch: fetchFrameworks };
};

// ============================================
// HOOK: CONSENSUS PRINCIPLES
// ============================================

export const useConsensusPrinciples = () => {
  const [data, setData] = useState<{
    principles: ConsensusPrinciple[];
    total_count: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPrinciples = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiBaseUrl()}/api/research-library/consensus-principles`, { headers });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load consensus principles');
      }
      const data = await response.json();
      setData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load consensus principles');
      console.error('Error fetching principles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrinciples();
  }, [fetchPrinciples]);

  return { data, loading, error, refetch: fetchPrinciples };
};

// ============================================
// HOOK: RESOURCE WITH FRAMEWORKS (DETAIL VIEW)
// ============================================

export const useResourceDetail = (resourceId: string | null) => {
  const [data, setData] = useState<{
    resource: Resource;
    frameworks: Framework[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResourceDetail = useCallback(async () => {
    if (!resourceId) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      
      // Fetch resource data
      const resourcesResponse = await fetch('/api/research-library/resources', { headers });
      if (!resourcesResponse.ok) {
        const errorData = await resourcesResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load resources');
      }
      const resourcesData = await resourcesResponse.json();
      const resource = resourcesData.resources.find((r: Resource) => r.id === resourceId);
      
      if (!resource) {
        throw new Error('Resource not found');
      }

      // Fetch frameworks for this resource
      const frameworksResponse = await fetch(`${getApiBaseUrl()}/api/research-library/resource/${resourceId}/frameworks`, { headers });
      if (!frameworksResponse.ok) {
        const errorData = await frameworksResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load frameworks');
      }
      const frameworksData = await frameworksResponse.json();
      
      setData({
        resource,
        frameworks: frameworksData.frameworks
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load resource details');
      console.error('Error fetching resource detail:', err);
    } finally {
      setLoading(false);
    }
  }, [resourceId]);

  useEffect(() => {
    fetchResourceDetail();
  }, [fetchResourceDetail]);

  return { data, loading, error, refetch: fetchResourceDetail };
};

// ============================================
// HOOK: FRAMEWORK SUPPORTING SOURCES
// ============================================
// 🆕 2026-04-29 (Phase 1): Lazily resolves a framework's primary_research_sources
// to full ResearchSource rows. Called from the expanded framework card in
// the Library tab when the user opens "Peer-Reviewed Sources".
//
// 🆕 2026-04-29 (Phase 2): Pass include_related=true to also surface tag-overlap
// matches across the full keyworded corpus (academic + practitioner). The
// knowledge_type_filter (default 'all') restricts that section by source type.

export type KnowledgeTypeFilter = 'all' | 'research' | 'practitioner';

export interface FrameworkSourcesResponse {
  framework_id: string;
  academic_sources: ResearchSource[];
  related_sources: ResearchSource[];
  include_related: boolean;
  knowledge_type_filter: string | null;
}

interface UseFrameworkSourcesOptions {
  includeRelated?: boolean;
  knowledgeTypeFilter?: KnowledgeTypeFilter;
  relatedLimit?: number;
}

// 🔧 2026-08-14 (native SWR cache): collapsing and re-expanding a framework card
// refetched every time — this hook had no cache, and `frameworkId` flips
// null→id on each expand, so the deps refire and the section spins again. Like
// useResearchSource below, a framework's source list is a static corpus read, so
// a hit is served from memory with no spinner and no refetch. The key carries
// the query shape (include_related / knowledge filter / limit) because each
// combination is a different response.
const _frameworkSourcesCache = new Map<string, FrameworkSourcesResponse>();

function _frameworkSourcesKey(
  frameworkId: string,
  includeRelated: boolean,
  knowledgeTypeFilter: KnowledgeTypeFilter,
  relatedLimit: number
): string {
  return `${frameworkId}|${includeRelated}|${knowledgeTypeFilter}|${relatedLimit}`;
}

export const useFrameworkSources = (
  frameworkId: string | null,
  options: UseFrameworkSourcesOptions = {}
) => {
  const includeRelated = options.includeRelated ?? false;
  const knowledgeTypeFilter = options.knowledgeTypeFilter ?? 'all';
  const relatedLimit = options.relatedLimit ?? 20;

  const [data, setData] = useState<FrameworkSourcesResponse | null>(() => {
    if (!frameworkId || !isNativeSwrCacheEnabled()) return null;
    const key = _frameworkSourcesKey(frameworkId, includeRelated, knowledgeTypeFilter, relatedLimit);
    return _frameworkSourcesCache.get(key) ?? null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSources = useCallback(async () => {
    if (!frameworkId) {
      setData(null);
      return;
    }
    const cacheKey = _frameworkSourcesKey(frameworkId, includeRelated, knowledgeTypeFilter, relatedLimit);
    if (isNativeSwrCacheEnabled()) {
      const cached = _frameworkSourcesCache.get(cacheKey);
      if (cached) {
        setData(cached);
        setError(null);
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({
        include_related: String(includeRelated),
        knowledge_type: knowledgeTypeFilter,
        related_limit: String(relatedLimit),
      });
      const response = await fetch(
        `${getApiBaseUrl()}/api/research-library/frameworks/${frameworkId}/sources?${params.toString()}`,
        { headers }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load framework sources');
      }
      const payload = (await response.json()) as FrameworkSourcesResponse;
      if (isNativeSwrCacheEnabled()) {
        _frameworkSourcesCache.set(cacheKey, payload);
      }
      setData(payload);
    } catch (err: any) {
      setError(err.message || 'Failed to load framework sources');
      console.error('Error fetching framework sources:', err);
    } finally {
      setLoading(false);
    }
  }, [frameworkId, includeRelated, knowledgeTypeFilter, relatedLimit]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  return { data, loading, error, refetch: fetchSources };
};

// ============================================
// HOOK: SINGLE RESEARCH SOURCE (FOR DETAIL SHEET)
// ============================================
// 🆕 2026-04-29: Knowledge-type-agnostic single source fetch. Powers the
// native ResearchSourceSheet AND the research-brief all-sources list.
// Returns `null` data while sourceId is null (sheet closed).
//
// 🔧 2026-07-01 (phase-2 followup): a research brief lists up to ~10 sources,
// each rendered by a SourceRow that calls this hook, and drilling into a source
// re-mounts the detail with the same id — so the same source used to be fetched
// many times. Module-level cache + in-flight dedup fix that: sources are static
// research records, so once fetched a sourceId is served from memory (no
// spinner, no refetch), and concurrent requests for the same id share one fetch.
const _sourceCache = new Map<string, ResearchSource>();
const _sourceInflight = new Map<string, Promise<ResearchSource>>();

async function _loadResearchSource(sourceId: string): Promise<ResearchSource> {
  const cached = _sourceCache.get(sourceId);
  if (cached) return cached;
  const inflight = _sourceInflight.get(sourceId);
  if (inflight) return inflight;
  const promise = (async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${getApiBaseUrl()}/api/research-library/sources/${sourceId}`,
      { headers }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to load source detail');
    }
    const src = (await response.json()) as ResearchSource;
    _sourceCache.set(sourceId, src);
    return src;
  })();
  _sourceInflight.set(sourceId, promise);
  try {
    return await promise;
  } finally {
    _sourceInflight.delete(sourceId);
  }
}

export const useResearchSource = (sourceId: string | null) => {
  const [data, setData] = useState<ResearchSource | null>(
    sourceId ? _sourceCache.get(sourceId) ?? null : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchSource = useCallback(async () => {
    if (!sourceId) {
      setData(null);
      return;
    }
    // Cache hit → resolve synchronously, no spinner, no refetch.
    const cached = _sourceCache.get(sourceId);
    if (cached) {
      setData(cached);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const src = await _loadResearchSource(sourceId);
      if (mountedRef.current) setData(src);
    } catch (err: any) {
      if (mountedRef.current) setError(err.message || 'Failed to load source detail');
      console.error('Error fetching source detail:', err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [sourceId]);

  useEffect(() => {
    fetchSource();
  }, [fetchSource]);

  return { data, loading, error, refetch: fetchSource };
};
